import React, { useRef, useState, useEffect } from 'react';
import 'prop-types';
import { connect, useSelector } from 'react-redux';
import { useWalletCalcConfig } from 'wallet-manager';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import clsx from 'clsx';
import { LoadingSpinner, Button, Text } from 'reaxl';
import { usePrevious } from '@atc/use-previous';
import { useFeatures } from '@atc/features';
import { getLowestPersonalizedPayment, calculateDownPayment, getPersonalizedPaymentParams, getPersonalizedPaymentDetails } from '@atc/wallet-payment-fns';
import PersonalPayment from '../PersonalPayment/PersonalPayment.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
function usePaymentData(_ref) {
  let {
    listing,
    isPreorder,
    listingId,
    paymentsInfo,
    listingPayment,
    creditType,
    loanTerm
  } = _ref;
  const listingPreorder = useSelector(state => _get(state, `preorder.${listingId}`)) || {};
  const listingCard = isPreorder ? listingPreorder : listing;
  const defaultPayment = getLowestPersonalizedPayment(listingPayment, creditType, loanTerm)?.payment;
  const salePrice = _get(listingCard, 'pricingDetail.salePrice', '');
  const msrp = _get(listingCard, 'pricingDetail.msrp', '');
  const price = salePrice || msrp;
  const {
    amountOwed = 0,
    value: tradeInValue = 0
  } = paymentsInfo.tradeIn || {};
  const totalEquity = calculateDownPayment(price, paymentsInfo) + (tradeInValue - amountOwed);
  const paymentServices = _get(listingCard, 'paymentServices', {});
  const {
    paymentCalculationUrl
  } = paymentServices;
  let listingType = (listingCard.type || listingCard.listingType || 'used').toLowerCase();
  if (listingType === 'certified') {
    listingType = 'used';
  }
  return {
    listingCard,
    defaultPayment,
    price,
    totalEquity,
    paymentCalculationUrl,
    listingType
  };
}
function useFetchConditions(_ref2) {
  let {
    paymentsInfo,
    showMyWalletModal,
    updateOnPaymentChange,
    uiContext,
    useCustomRate
  } = _ref2;
  const prevShowModal = usePrevious(showMyWalletModal, false);
  const prevPaymentsInfo = usePrevious(paymentsInfo, {});
  const fetchOnPaymentInfoChange = updateOnPaymentChange && !_isEqual(prevPaymentsInfo, paymentsInfo);
  const fetchOnWalletModalClose = !updateOnPaymentChange && prevShowModal && !showMyWalletModal;
  const showRates = uiContext === 'withRates' || uiContext === 'default';
  const shouldUpdatePayment = fetchOnPaymentInfoChange || fetchOnWalletModalClose || showRates;
  const shouldFetchPayment = !useCustomRate && shouldUpdatePayment;
  return {
    shouldFetchPayment,
    shouldUpdatePayment
  };
}
function useLeasePaymentLogic(_ref3) {
  let {
    listingPayment,
    paymentSettings: {
      creditType,
      leaseTerm,
      enablePaymentOption,
      paymentOption
    },
    personalizedPayment,
    uiContext,
    paymentsInfo
  } = _ref3;
  const leasePayment = listingPayment?.rates?.[creditType]?.leaseOffers?.[leaseTerm]?.[0]?.payment;
  const isLeasePaymentOptionSelected = enablePaymentOption && paymentOption === 'lease';
  const isLeasePaymentLower = !!leasePayment && leasePayment < personalizedPayment;
  const paymentEmphasis = paymentsInfo?.interactions?.paymentEmphasis;
  const showLeaseByDefault = uiContext === 'default' && isLeasePaymentLower && !paymentEmphasis;
  function getPersonalizedPayment() {
    if ((isLeasePaymentOptionSelected || showLeaseByDefault) && leasePayment) {
      return leasePayment;
    }
    return personalizedPayment;
  }
  return {
    leasePayment,
    isLeasePaymentOptionSelected,
    showLeaseByDefault,
    getPersonalizedPayment
  };
}

// Helper function to handle payment fetching logic
function usePaymentFetching(_ref4) {
  let {
    fetchPayments,
    paymentsEnabled,
    paymentCalculationUrl,
    listingId,
    useCustomRate,
    enablePaymentOption,
    leaseTerm,
    leaseMileageRange,
    price,
    paymentsInfo,
    disableTrade,
    tradeIn,
    finalUserZip,
    creditType,
    loanTerm,
    updateListingPayments,
    setPersonalizedPayment,
    setIsLoading,
    resetPaymentState,
    isInitialFetch
  } = _ref4;
  const fetchPersonalizedPayment = async () => {
    if (!fetchPayments || !paymentsEnabled || !paymentCalculationUrl || !listingId || useCustomRate) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const downPaymentValue = calculateDownPayment(price, paymentsInfo);
    let leaseParams = {};
    if (enablePaymentOption) {
      leaseParams = {
        leaseTerm,
        milesPerYear: leaseMileageRange,
        leaseDownPayment: downPaymentValue
      };
    }
    const personalizedPaymentsInfo = {
      ...paymentsInfo,
      ...leaseParams,
      includeCash: true,
      tradeIn: disableTrade ? undefined : tradeIn
    };
    const personalizedPaymentParams = getPersonalizedPaymentParams({
      paymentsInfo: personalizedPaymentsInfo,
      userZip: finalUserZip
    });
    const {
      details,
      error
    } = await getPersonalizedPaymentDetails({
      fetchPayments,
      paymentCalculationUrl,
      personalizedPaymentParams
    });
    if (error || details?.error) {
      resetPaymentState();
    } else if (details?.rates && Object.keys(details.rates).length) {
      const {
        payment
      } = await getLowestPersonalizedPayment(details, creditType, loanTerm);
      if (updateListingPayments && payment) {
        setPersonalizedPayment(payment);
        updateListingPayments({
          [listingId]: {
            ...details
          }
        });
      }
    } else {
      resetPaymentState();
    }
    isInitialFetch.current = false;
    setIsLoading(false);
  };
  return fetchPersonalizedPayment;
}

// Helper function to handle zero payments scenario
function handleZeroPayments(_ref5) {
  let {
    setIsLoading,
    setPersonalizedPayment,
    updateListingPayments,
    listingId
  } = _ref5;
  setIsLoading(false);
  setPersonalizedPayment(0);
  if (updateListingPayments) {
    updateListingPayments({
      [listingId]: {
        payment: 0
      }
    });
  }
}

// Helper function to render lease contact message
function renderLeaseContactMessage(listingType) {
  return listingType === 'new' ? /*#__PURE__*/jsxs("div", {
    className: "text-size-200",
    "data-cmp": "lnk-pymt-dtls",
    children: ["Contact Dealer\xA0", /*#__PURE__*/jsx("br", {}), "for Leasing Options"]
  }) : null;
}

// Helper function to render payment content
function renderPaymentContent(_ref6) {
  let {
    uiContext,
    paymentsInfo,
    price,
    listingType,
    getPersonalizedPayment,
    shouldUpdatePayment,
    listingId,
    rest
  } = _ref6;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [['withRates', 'ribbon'].includes(uiContext) && /*#__PURE__*/jsxs(Text, {
      componentClass: "span",
      ...(uiContext === 'withRates' ? {
        color: 'subdued-lighter'
      } : {}),
      ...(uiContext === 'withRates' ? {
        size: 200
      } : {}),
      children: [uiContext === 'withRates' ? 'Est. Payment' : 'Est.', "\xA0"]
    }), /*#__PURE__*/jsx(PersonalPayment, {
      paymentsInfo: paymentsInfo,
      price: price,
      ...(uiContext !== 'ribbon' ? {
        textColor: 'gray-base'
      } : {}),
      vehicleCondition: listingType,
      uiContext: uiContext === 'default' ? 'withRates' : uiContext,
      personalizedPayment: getPersonalizedPayment(),
      shouldUpdatePayment: shouldUpdatePayment,
      ...rest
    }, `personalPayment-${listingId}`), !['default', 'ribbon'].includes(uiContext) && /*#__PURE__*/jsx(Button, {
      bsStyle: "text",
      block: uiContext !== 'default',
      className: "text-size-200 margin-left-2",
      style: {
        paddingTop: '2px'
      },
      children: "See details"
    })]
  });
}
const WalletPaymentDetailsV2 = /*#__PURE__*/React.memo(_ref7 => {
  let {
    listing,
    isPreorder,
    isCompare,
    listingId,
    listingPayment,
    onMyWalletClick,
    paymentsInfo = {},
    paymentOption,
    personalPaymentProps = {},
    showMyWalletModal,
    userZip,
    updateListingPayments,
    updateOnPaymentChange = false,
    uiContext = 'ribbon',
    ...rest
  } = _ref7;
  const {
    wallet_payment_option: [enablePaymentOption]
  } = useFeatures(['wallet_payment_option']);
  const {
    loanTerm,
    creditType,
    tradeIn = {},
    leaseTerm,
    leaseMileageRange
  } = paymentsInfo;
  const {
    disableTrade
  } = useWalletCalcConfig(paymentsInfo, listing);
  const isInitialFetch = useRef(true);
  const {
    fetcher: fetchPayments,
    paymentsEnabled = true
  } = personalPaymentProps;
  const [personalizedPayment, setPersonalizedPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const resetPaymentState = () => {
    if (updateListingPayments) {
      updateListingPayments({
        [listingId]: null
      });
    }
    setPersonalizedPayment(null);
  };
  const {
    listingCard,
    price,
    totalEquity,
    paymentCalculationUrl,
    listingType
  } = usePaymentData({
    listing,
    isPreorder,
    listingId,
    paymentsInfo,
    listingPayment,
    creditType,
    loanTerm
  });
  let finalUserZip = userZip;
  if (finalUserZip === null) {
    finalUserZip = _get(listingCard, 'zip', null);
  }
  const {
    custom: customRate
  } = paymentsInfo?.interestRate || {};
  const useCustomRate = typeof customRate === 'number';
  const prevListingId = usePrevious(listingId, null);
  const {
    shouldFetchPayment,
    shouldUpdatePayment
  } = useFetchConditions({
    paymentsInfo,
    showMyWalletModal,
    updateOnPaymentChange,
    uiContext,
    useCustomRate
  });
  const {
    leasePayment,
    isLeasePaymentOptionSelected,
    getPersonalizedPayment
  } = useLeasePaymentLogic({
    listingPayment,
    paymentSettings: {
      creditType,
      leaseTerm,
      enablePaymentOption,
      paymentOption
    },
    personalizedPayment,
    uiContext,
    paymentsInfo
  });
  const zeroPayments = !price || totalEquity >= price;
  const fetchPersonalizedPayment = usePaymentFetching({
    fetchPayments,
    paymentsEnabled,
    paymentCalculationUrl,
    listingId,
    useCustomRate,
    enablePaymentOption,
    leaseTerm,
    leaseMileageRange,
    price,
    paymentsInfo,
    disableTrade,
    tradeIn,
    finalUserZip,
    creditType,
    loanTerm,
    updateListingPayments,
    setPersonalizedPayment,
    setIsLoading,
    resetPaymentState,
    isInitialFetch
  });
  useEffect(() => {
    if (useCustomRate) {
      setIsLoading(false);
      setPersonalizedPayment(null);
    }
    if (prevListingId !== listingId) {
      isInitialFetch.current = true;
    }
    if (shouldFetchPayment || isInitialFetch.current) {
      if (zeroPayments) {
        handleZeroPayments({
          setIsLoading,
          setPersonalizedPayment,
          updateListingPayments,
          listingId
        });
      } else {
        fetchPersonalizedPayment();
      }
    }
  }, [listingId, paymentsInfo, finalUserZip, useCustomRate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading && listingId) {
    return /*#__PURE__*/jsx(LoadingSpinner, {
      className: "margin-0"
    });
  }
  if (isLeasePaymentOptionSelected && !leasePayment) {
    return renderLeaseContactMessage(listingType);
  }
  const showRates = uiContext === 'withRates' || uiContext === 'default';
  if (!isLoading && listingId && price) {
    const children = renderPaymentContent({
      uiContext,
      paymentsInfo,
      price,
      listingType,
      getPersonalizedPayment,
      shouldUpdatePayment,
      listingId,
      rest
    });
    return /*#__PURE__*/jsx(Button, {
      bsStyle: "text",
      onClick: onMyWalletClick,
      className: clsx('price-version-2', {
        'border-right-1': showRates,
        'text-link link-undecorated text-size-200': uiContext !== 'withRates',
        'display-flex align-items-center': uiContext !== 'default'
      }),
      "data-cmp": "lnk-pymt-dtls",
      tabIndex: "0",
      children: children
    }, "renderPricingModalTrigger");
  }
  return null;
}, (prevProps, nextProps) => _isEqual(prevProps.paymentsInfo, nextProps.paymentsInfo) && _isEqual(prevProps.listingPayments, nextProps.listingPayments) && _isEqual(prevProps.listingPayment, nextProps.listingPayment) && _isEqual(prevProps.showMyWalletModal, nextProps.showMyWalletModal) && _isEqual(prevProps.listing.paymentServices?.paymentCalculationUrl, nextProps.listing.paymentServices?.paymentCalculationUrl));
function mapStateToProps(state, _ref8) {
  let {
    listingId
  } = _ref8;
  const {
    inventory = {},
    payments = {}
  } = state;
  const {
    listingPayments = {},
    myWallet = {},
    paymentOption,
    ...paymentsRest
  } = payments;
  const {
    displayModal: showMyWalletModal
  } = myWallet;
  const listing = inventory[listingId] || {};
  const listingPayment = listingPayments[listingId] || {};
  const userZip = _get(state, 'user.location.zip', null);

  // NOTE: These are causing potenially uneeded rerenders
  delete paymentsRest.penciling;
  delete paymentsRest.listingLeasePayments;
  delete paymentsRest.listingCashPayments;
  delete paymentsRest.dealType;
  delete paymentsRest.icoTradeIn;
  return {
    listing,
    listingPayment,
    showMyWalletModal,
    paymentsInfo: paymentsRest,
    paymentOption,
    userZip
  };
}
var WalletPaymentDetailsV2$1 = /* #__PURE__*/connect(mapStateToProps)(WalletPaymentDetailsV2);
export { WalletPaymentDetailsV2$1 as default };
